/**
 * Sprint V102.ab (02.10.2026) — QuickCheck v2-Komponente (Antreiber-Fokus).
 *
 * Unterschiede zu `QuickCheckV100.jsx`:
 *   - KEIN Kontext-Switcher (Direktstart in Q1 nach Mount)
 *   - Q1-Q4: Likert via bestehende `QuickCheckLikertQuestion`
 *   - Q5: Forced-Choice (ABCD) via neue `QuickCheckForcedChoiceQuestion`
 *   - Submit mit `question_set="v2"` + `context="universal"` + Antworten
 *     als Mix aus `{qid, value}` und `{qid, choice}`.
 *   - Tracking-Events (test_start/question_answered/test_complete) führen
 *     `question_set=v2` + `primary_type` + `score_lead` mit.
 *
 * Scoring passiert ausschließlich im Backend (`routes_quickcheck.submit`),
 * sodass das Frontend keine Scoring-Logik duplizieren muss.
 */
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { trackFokustypAssigned } from "@/lib/ga4";
import { QUICKCHECK_V102 } from "../../data/quickCheckV102Data";
import QuickCheckLikertQuestion from "./QuickCheckLikertQuestion";
import QuickCheckResult from "./QuickCheckResult";

const V99_TEST_NAME = "v99_bridge_video";
const ADVANCE_DELAY_MS = 250;

function extractUTM() {
  try {
    const params = new URLSearchParams(window.location.search);
    const out = {};
    ["source", "medium", "campaign", "content", "term"].forEach((k) => {
      const v = params.get(`utm_${k}`);
      if (v) out[k] = v;
    });
    return Object.keys(out).length ? out : null;
  } catch {
    return null;
  }
}

function getOrCreateSessionId() {
  try {
    let sid = localStorage.getItem("f4s_session_id");
    if (!sid) {
      sid = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem("f4s_session_id", sid);
    }
    return sid;
  } catch {
    return `s_${Date.now()}`;
  }
}

function trackGA4(eventName, params) {
  try {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", eventName, params || {});
    }
  } catch {
    /* noop */
  }
}

export default function QuickCheckV102({
  source = "landing_page_quicktest",
  onMagicLink,
  onBackToCockpit,
}) {
  // Direktstart: step beginnt mit "questions" (kein Kontext-Chooser).
  const [step, setStep] = useState("questions");
  const [answers, setAnswers] = useState({}); // { qid: value | choice }
  const [index, setIndex] = useState(0);
  const [pendingValue, setPendingValue] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [abVariant, setAbVariant] = useState(null);

  const sessionId = useMemo(getOrCreateSessionId, []);
  const utm = useMemo(extractUTM, []);

  const questions = QUICKCHECK_V102;
  const total = questions.length;
  const currentQ = questions[index];

  // test_start einmalig beim Mount feuern (das ist in v2 auch Frage 1).
  // 05.10.2026 — Namens-Konsistenz-Fix: v1-Flow feuert via trackTestStart
  // (ga4.js:205) den Event `test_start` ohne „ed"-Suffix. v2 nutzte
  // faelschlich `test_started` → GA4 sah den A/B-Split als zwei
  // getrennte Events.
  useEffect(() => {
    trackGA4("test_start", { question_set: "v2", source });
    trackGA4("quickcheck_v102_view", { source });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A/B-Test-V99 Assignment (für Video-Bridge nach Ergebnis).
  useEffect(() => {
    if (!sessionId) return;
    api
      .get(`/ab-test/assign`, {
        params: { session_id: sessionId, test: V99_TEST_NAME },
      })
      .then((r) => setAbVariant(r?.data?.variant || null))
      .catch(() => setAbVariant(null));
  }, [sessionId]);

  const submitToBackend = async (finalAnswers) => {
    setSubmitting(true);
    setError(null);
    try {
      // v2.1 (03.10.2026) — alle 5 Items sind Likert, einfaches
      // numerisches Payload-Schema [{qid, value}].
      const answersPayload = questions.map((q) => ({
        qid: q.qid,
        value: finalAnswers[q.qid],
      }));
      const payload = {
        session_id: sessionId,
        context: "universal", // v2 hat keinen beruflich/privat-Switcher
        answers: answersPayload,
        utm,
        ab_variant_v99: abVariant || null,
        source,
        question_set: "v2",
      };
      const { data } = await api.post("/quickcheck/v100/submit", payload);
      setResult(data);
      setStep("result");

      // 05.10.2026 — Namens-Konsistenz-Fix: v1-Flow feuert via
      // `trackTestComplete` (ga4.js:212) den Event `test_complete`
      // (ohne „d"). v2 nutzte faelschlich `test_completed` → GA4
      // sah den A/B-Split als zwei getrennte Events. Jetzt einheitlich.
      trackGA4("test_complete", {
        question_set: "v2",
        assigned_primary_type: data.primary_type,
        score_lead: data.fokustyp_score_delta,
        variant_v99: abVariant || "unassigned",
      });
      trackFokustypAssigned({ fokustyp: data.primary_type, questionSet: "v2" });
      // Dedicated result_viewed-Event folgt direkt (die Komponente wird
      // mit dem Result-Step gerendert).
      trackGA4("result_viewed", {
        question_set: "v2",
        assigned_primary_type: data.primary_type,
      });

      try {
        await api.post("/ab-test/track/impression", {
          session_id: sessionId,
          test_name: V99_TEST_NAME,
          variant: abVariant || "A",
          fokustyp: data.primary_type,
        });
      } catch {
        /* fire-and-forget */
      }
    } catch (exc) {
      setError(
        exc?.response?.data?.detail ||
          "Etwas ging schief. Bitte versuch es gleich nochmal.",
      );
      setStep("error");
    } finally {
      setSubmitting(false);
      setPendingValue(null);
    }
  };

  // Auto-Advance Handler für Likert (Q1-Q4) und ForcedChoice (Q5).
  const handleAnswer = (answerValue) => {
    if (pendingValue || submitting || !currentQ) return;
    setPendingValue(answerValue);
    const next = { ...answers, [currentQ.qid]: answerValue };
    setAnswers(next);
    trackGA4("quickcheck_v102_question_answered", {
      qid: currentQ.qid,
      question_set: "v2",
      answer: answerValue,
    });

    window.setTimeout(() => {
      if (index < total - 1) {
        setIndex((i) => i + 1);
        setPendingValue(null);
      } else {
        submitToBackend(next);
      }
    }, ADVANCE_DELAY_MS);
  };

  const handleBack = () => {
    if (index === 0 || pendingValue || submitting) return;
    setIndex((i) => i - 1);
    setPendingValue(null);
  };

  const handleRestart = () => {
    setStep("questions");
    setAnswers({});
    setIndex(0);
    setPendingValue(null);
    setResult(null);
    setError(null);
  };

  return (
    <div
      className="w-full"
      data-testid="quickcheck-v102-root"
      data-question-set="v2"
    >
      {step === "questions" && currentQ && (
        <div className="max-w-3xl mx-auto">
          <QuickCheckLikertQuestion
            qid={currentQ.qid}
            qIndex={index}
            qTotal={total}
            text={currentQ.text}
            value={answers[currentQ.qid] || null}
            pendingValue={pendingValue}
            submitting={submitting}
            onChange={handleAnswer}
            onBack={handleBack}
          />
        </div>
      )}

      {step === "result" && result && (
        <QuickCheckResult
          primaryType={result.primary_type}
          secondaryType={result.secondary_type}
          scores={result.scores}
          onRestart={handleRestart}
          variant={abVariant}
          onMagicLink={onMagicLink}
          onBackToCockpit={onBackToCockpit}
          followUpMode={source === "dashboard_test" ? "dashboard" : "landing"}
        />
      )}

      {step === "error" && (
        <div
          className="max-w-md mx-auto text-center bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-2xl p-6"
          data-testid="v102-error"
        >
          <p className="text-red-700 dark:text-red-300">{error}</p>
          <button
            type="button"
            onClick={handleRestart}
            className="mt-4 rounded-full bg-slate-900 dark:bg-white text-white dark:text-slate-900 px-6 py-2 text-sm font-bold"
          >
            Nochmal starten
          </button>
        </div>
      )}
    </div>
  );
}
